import { useNavigate } from 'react-router-dom'
import { Hourglass, Loader2 } from 'lucide-react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { usePendingInventory } from '@/hooks/useInventoryMaster'
import { STATUS_LABELS, STATUS_STYLES } from '@/components/inventory/MaterialsTable'
import { formatSheetDateTime } from '@/lib/pickList'
import {
  pendingProgressPct,
  pendingRunHref,
  sortPendingRowsByReceived,
} from '@/lib/pendingInventory'
import type { PendingRow } from '@/types/inventory'

/**
 * Inventory → Pending Inventory.
 *
 * One row per received LOT still short of release. Per lot rather than per
 * material because that is where quarantine lives: two deliveries of the same
 * material are two separate decisions, and collapsing them would hide the one
 * still outstanding behind the one that cleared.
 *
 * A row leaves by itself once its lot clears — no dismiss action, no "done"
 * flag — so unlike Kits this is a single list with no sub-tabs, no KPI cards
 * and no pagination.
 */
export function PendingInventoryTab() {
  const { data, isLoading, error } = usePendingInventory()
  const rows = sortPendingRowsByReceived(data ?? [])

  return (
    <div className='space-y-4'>
      <p className='text-xs text-muted-foreground'>
        Stock received against a connected Inventory procedure, still short of release. Open a row
        to work that lot's forms.
      </p>

      {isLoading ? (
        <div className='flex items-center gap-2 p-8 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Loading pending inventory…
        </div>
      ) : error ? (
        <p className='text-sm text-destructive'>
          {error instanceof Error ? error.message : 'Could not load pending inventory.'}
        </p>
      ) : (
        <PendingTable rows={rows} />
      )}
    </div>
  )
}

// Per-user column layout. Lot and the actions cell are locked.
const PENDING_TABLE_ID = 'inventory-pending'

const PENDING_COLUMNS: TableColumnDef[] = [
  { id: 'lot', header: 'Lot', locked: true, sortable: true },
  { id: 'material', header: 'Material', sortable: true },
  { id: 'status', header: 'Status', sortable: true },
  { id: 'procedure', header: 'Procedure', sortable: true },
  { id: 'progress', header: 'Form progress', sortable: true },
  { id: 'received', header: 'Received', sortable: true },
  { id: 'actions', header: 'Open', locked: true },
]

// Header sorting (z8nrz7e4h6). Unsorted, rows keep the received order.
const PENDING_SORT: SortAccessors<PendingRow> = {
  lot: r => r.lotNumber,
  material: r => r.materialId,
  status: r => STATUS_LABELS[r.gateStatus as keyof typeof STATUS_LABELS] ?? r.gateStatus,
  // A deleted run shows "run deleted", so it sorts with the blanks.
  procedure: r => r.procedureTemplateName,
  progress: r => pendingProgressPct(r),
  received: r => r.receivedAt,
}

const PENDING_HEAD_CLASSES: Record<string, string | undefined> = {
  actions: 'w-16',
}

function PendingTable({ rows }: { rows: PendingRow[] }) {
  // Before the empty-state early return: a hook cannot be called conditionally.
  const cols = useTableColumns(PENDING_TABLE_ID, PENDING_COLUMNS)
  const sortedRows = useSortedRows(rows, cols.sort, PENDING_SORT)
  const navigate = useNavigate()

  if (rows.length === 0) {
    return (
      <div className='rounded-xl border bg-card p-10 text-center'>
        <Hourglass className='mx-auto h-6 w-6 text-muted-foreground' />
        <p className='mt-2 text-sm font-medium'>Nothing is awaiting release</p>
        <p className='mt-1 text-xs text-muted-foreground'>
          A lot received against a connected Inventory procedure appears here until that
          procedure's forms are complete.
        </p>
      </div>
    )
  }

  return (
    <div className='rounded-xl border bg-card'>
      {/* The shared shadcn table, not a hand-rolled one: `TableHeader` carries
          the app's `bg-table-header` token, so this tab's header matches the
          material tables instead of drifting to its own muted grey. */}
      <Table className='min-w-[900px]'>
        <TableHeader>
          <TableRow>
            {cols.visible.map(({ def }) => (
              <ResizableTableHead
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                menuLabel='Pending columns'
                className={PENDING_HEAD_CLASSES[def.id] ?? 'whitespace-nowrap'}
                resizable={def.id !== 'actions'}
              >
                {def.header}
              </ResizableTableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.map((row, i) => {
            const href = pendingRunHref(row)
            const pct = pendingProgressPct(row)
            return (
              <TableRow
                key={row.lotId}
                // A row whose run has been deleted is not a link: there is
                // nothing to open, and a dead click reads as a broken page.
                role={href ? 'link' : undefined}
                tabIndex={href ? 0 : undefined}
                onClick={href ? () => navigate(href) : undefined}
                onKeyDown={
                  href
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          navigate(href)
                        }
                      }
                    : undefined
                }
                className={cn(
                  href
                    ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset'
                    : 'cursor-default',
                  // Zebra striping only; hover, borders and the base row colour
                  // come from TableRow so this matches the material tables.
                  i % 2 === 1 && 'bg-muted/10',
                )}
              >
                <ColumnCells
                  columns={cols}
                  cells={{
                    lot: (
                      <TableCell>
                        <div className='font-mono text-xs font-semibold text-gray-900'>
                          {row.lotNumber}
                        </div>
                        {row.vendorLotNumber && (
                          <div className='font-mono text-[11px] text-muted-foreground'>
                            {row.vendorLotNumber}
                          </div>
                        )}
                      </TableCell>
                    ),
                    material: (
                      <TableCell>
                        <div className='font-semibold text-gray-900'>{row.materialId}</div>
                        <div className='text-xs text-muted-foreground'>{row.materialName}</div>
                      </TableCell>
                    ),
                    status: (
                      <TableCell>
                        <span
                          className={cn(
                            'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
                            STATUS_STYLES[row.gateStatus as keyof typeof STATUS_STYLES] ??
                              'bg-gray-100 text-gray-500',
                          )}
                        >
                          {STATUS_LABELS[row.gateStatus as keyof typeof STATUS_LABELS] ??
                            row.gateStatus}
                        </span>
                      </TableCell>
                    ),
                    procedure: (
                      <TableCell>
                        {row.procedureTemplateName ? (
                          <span className='font-medium text-gray-900'>
                            {row.procedureTemplateName}
                          </span>
                        ) : (
                          <span
                            className='text-xs italic text-muted-foreground'
                            title='The run that gated this lot has been deleted. The stock keeps the status it had reached.'
                          >
                            run deleted
                          </span>
                        )}
                      </TableCell>
                    ),
                    progress: (
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          <div className='h-1.5 w-20 overflow-hidden rounded-full bg-slate-200'>
                            <div
                              className='h-full rounded-full bg-emerald-500'
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className='text-xs tabular-nums text-muted-foreground'>
                            {row.completedForms}/{row.totalForms}
                          </span>
                        </div>
                      </TableCell>
                    ),
                    received: (
                      <TableCell className='whitespace-nowrap tabular-nums text-gray-600'>
                        {formatSheetDateTime(row.receivedAt)}
                      </TableCell>
                    ),
                    actions: (
                      <TableCell className='text-right'>
                        <span className='text-xs text-muted-foreground'>
                          {href ? 'Open' : '—'}
                        </span>
                      </TableCell>
                    ),
                  }}
                />
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
